/**
 * EnhancedCuisineChip
 *
 * Interactive cuisine chip with:
 * - Cuisine flag and name
 * - TasteAura gradient background
 * - Hover and tap animations (matching restaurant profile style)
 */
import { useCallback } from 'react'
import { Box, Text, HStack } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { Capacitor } from '@capacitor/core'
import { Browser } from '@capacitor/browser'
import { generateCuisineChipStyle } from '@/utils/cuisineColorMapping'
import { analytics } from '@/lib/analytics'
import { AnalyticsEvents } from '@/lib/analytics/events'

const MotionBox = motion.create(Box)

export interface EnhancedCuisineChipProps {
  /** Cuisine name */
  name: string
  /** Cuisine flag emoji */
  flag: string
  /** Optional URL to open when the chip is clicked */
  learnMoreUrl?: string
}

function useLearnMoreHandler(learnMoreUrl: string | undefined, name: string) {
  return useCallback(async () => {
    if (!learnMoreUrl) return
    analytics.capture(AnalyticsEvents.CUISINE_CHIP_LEARN_MORE_CLICKED, { cuisine_name: name })
    try {
      if (Capacitor.isNativePlatform()) {
        await Browser.open({ url: learnMoreUrl })
      } else {
        window.open(learnMoreUrl, '_blank', 'noopener,noreferrer')
      }
    } catch (error) {
      console.error('Failed to open learn more URL:', error)
    }
  }, [learnMoreUrl, name])
}

export function EnhancedCuisineChip({ name, flag, learnMoreUrl }: EnhancedCuisineChipProps) {
  const chipStyle = generateCuisineChipStyle(name)
  const handleClick = useLearnMoreHandler(learnMoreUrl, name)

  return (
    <MotionBox
      bg={chipStyle.gradient}
      borderRadius="full"
      px={3}
      py={2}
      boxShadow="0 2px 8px var(--color-overlay-20)"
      cursor={learnMoreUrl ? 'pointer' : 'default'}
      onClick={handleClick}
      whileHover={{
        scale: 1.08,
        boxShadow: '0 4px 12px var(--color-overlay-40)',
        transition: { type: 'spring', stiffness: 400, damping: 15 },
      }}
      whileTap={{
        scale: 0.92,
        transition: { type: 'spring', stiffness: 600, damping: 20 },
      }}
    >
      <HStack spacing={2}>
        <Text fontSize="md">{flag}</Text>
        <Text
          color={chipStyle.textColor}
          fontWeight="semibold"
          fontSize="sm"
          textShadow={chipStyle.textShadow}
        >
          {name}
        </Text>
      </HStack>
    </MotionBox>
  )
}

/**
 * Compact version of EnhancedCuisineChip for smaller displays.
 */
export function EnhancedCuisineChipCompact({ name, flag, learnMoreUrl }: EnhancedCuisineChipProps) {
  const chipStyle = generateCuisineChipStyle(name)
  const handleClick = useLearnMoreHandler(learnMoreUrl, name)

  return (
    <MotionBox
      bg={chipStyle.gradient}
      borderRadius="full"
      px={3}
      py={1.5}
      display="inline-flex"
      alignItems="center"
      gap={2}
      cursor={learnMoreUrl ? 'pointer' : 'default'}
      onClick={handleClick}
      whileHover={{
        scale: 1.08,
        boxShadow: '0 4px 12px var(--color-overlay-40)',
        transition: { type: 'spring', stiffness: 400, damping: 15 },
      }}
      whileTap={{
        scale: 0.92,
        transition: { type: 'spring', stiffness: 600, damping: 20 },
      }}
    >
      <Text fontSize="sm">{flag}</Text>
      <Text
        color={chipStyle.textColor}
        fontWeight="medium"
        fontSize="sm"
        textShadow={chipStyle.textShadow}
      >
        {name}
      </Text>
    </MotionBox>
  )
}
